import type { ChangeEvent, FC, ReactNode } from 'react'

import { useCallback, useRef } from 'react'
import dynamic from 'next/dynamic'
import { Seo } from '@/components/generals/seo'
import { useDashboard } from '@/lib/store'
import { useI18n } from '@/lib/i18n/context'
import { LoadScreen } from '@/components/dashboard/load-screen'
import { siteConfig } from '@/constants/site'

// The dashboard code is only needed once a workbook is loaded
const AppShell = dynamic(() => import('@/components/dashboard/app-shell'), {
    ssr: false,
    loading: () => <div className='min-h-screen bg-background' />
})

const Home: FC = (): ReactNode => {
    const { dataset, loadFile } = useDashboard()
    const { locale } = useI18n()
    const inputRef = useRef<HTMLInputElement>(null)

    const pick = useCallback(() => inputRef.current?.click(), [])

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]

        // Reset so choosing the same file again still triggers a load
        event.target.value = ''

        if (file) void loadFile(file)
    }

    return (
        <>
            <Seo description={locale === 'en' ? siteConfig.descriptionEn : siteConfig.description} />

            <input
                ref={inputRef}
                type='file'
                accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                className='hidden'
                tabIndex={-1}
                aria-hidden='true'
                onChange={onChange}
            />

            {dataset ? <AppShell onPick={pick} /> : <LoadScreen onPick={pick} />}
        </>
    )
}

export default Home
